import get from "lodash/get";

import * as MessagingApi from "apps/messenger/util/web";
import OkC from "shared/util/OkcApi";
import Analytics from "analytics/util/Analytics";
import CUSTOM_EVENT_TYPES from "analytics/util/CustomEventTypes";
import * as ProfileActions from "shared/util/ProfileActions";
import GetUserIdFromWindowLocation from "shared/util/GetUserIdFromWindowLocation";
import Context from "shared/util/Context";
import { getValueForPromoDesign } from "analytics/util/queryParamsMap";
import Report, { TYPES } from "shared/util/Report";
import redirectToWhereTheyCameFrom from "shared/util/redirectToWhereTheyCameFrom";

import {
	OKEVENT,
	subscribeToOkEvent,
} from "shared/util/OkEventHelpers";
import {
	getUserInfo,
	setSelfview,
} from "ducks/profile_questions";
import {
	processProfileVote,
	removeUserFromQueues,
} from "ducks/quickmatch";

export const INITIAL_STATE = {
	isLoggedIn: false,
	isLoadingProfile: true,

	// profile fields
	location: {},

	essays: [
		/*
			{
				raw_content: "",
				clean_content: "",
				content: "",
				id: 0,
				placeholder: "",
				title: ""
			}
		*/
	],

	likes: {
		they_like: 0,
		you_like: 0,
		mutual_like: 0,
		passed_on: 0,
		recycled: 0,
	},

	percentages: {
		match: 0,
		enemy: 0,
	},

	firstMessage: null,

	blocked: false,
	userid: "",
	username: "",

	isBlocked: false,
	signupLink: null,

	isLoadingLike: false,
	isLoadingPass: false,
	isLoadingMessage: false,

	userGuides: {
		OCS_LIKE: false,
		OCS_PASS: false,
	},

	userInstructions: {
		/*
			instructionName: {
				id: "",
				ctaText: "",
				cancelText: "",
				userGuide: "",
				title: "",
				body: "",
			},
		*/
	},

	currentUserInstruction: null,

	showLikeExplainer: false,
	showUnmatchExplainer: false,
	showPassExplainer: false,
	showPassConfirmer: false,
	showMessagePrompt: false,

	confirmedPass: false,


	showNoPhotosBlock: false,
	mobileMessageLink: "",

	isLikesCapModalOpen: false,
	likesCapRateCardViewCount: 0,
};

function getInitialState() {
	const newState = {
		...INITIAL_STATE,
		...(window.profileParams ? window.profileParams.profile : {}),
	};

	// make sure some shit are present
	if (!newState.percentages) {
		newState.percentages = {};
	}

	if (!newState.likes) {
		newState.likes = {};
	}

	if (!newState.essays) {
		newState.essays = [];
	}

	return newState;
}


// action types
// ----------------------------------------------------------------------------


export const PROFILE_LOAD_INITIATED = "PROFILE_MW.PROFILE_LOAD_INITIATED";
export const LIKE_INITIATED = "PROFILE_MW.LIKE_INITIATED";
export const PASS_INTIATED = "PROFILE_MW.PASS_INTIATED";

export const PROFILE_LOAD_SUCCESS = "PROFILE_MW.PROFILE_LOAD_SUCCESS";
export const LIKE_SUCCESS = "PROFILE_MW.LIKE_SUCCESS";
export const PASS_SUCCESS = "PROFILE_MW.PASS_SUCCESS";

export const PROFILE_LOAD_CANCELED = "PROFILE_MW.PROFILE_LOAD_CANCELED";
export const LIKE_CANCELED = "PROFILE_MW.LIKE_CANCELED";
export const PASS_CANCELED = "PROFILE_MW.PASS_CANCELED";

export const PASS_CONFIRMED = "PROFILE_MW.PASS_CONFIRMED";
export const LIKE_EXPLAINED = "PROFILE_MW.LIKE_EXPLAINED";
export const PASS_EXPLAINED = "PROFILE_MW.PASS_EXPLAINED";
export const MESSAGE_PROMPT_DISMISSED = "PROFILE_MW.MESSAGE_PROMPT_DISMISSED";

export const BLOCK_INITIATED = "PROFILE_MW.BLOCK_INITIATED";
export const BLOCK_SUCCESS = "PROFILE_MW.BLOCK_SUCCESS";
export const UNBLOCK_INITIATED = "PROFILE_MW.UNBLOCK_INITIATED";
export const UNBLOCK_SUCCESS = "PROFILE_MW.UNBLOCK_SUCCESS";
export const REPORT_INITIATED = "PROFILE_MW.REPORT_INITIATED";

export const SHOW_LIKE_EXPLAINER = "PROFILE_MW.SHOW_LIKE_EXPLAINER";
export const TOGGLE_UNMATCH_EXPLAINER = "PROFILE_MW.TOGGLE_UNMATCH_EXPLAINER";
export const SHOW_PASS_EXPLAINER = "PROFILE_MW.SHOW_PASS_EXPLAINER";
export const SHOW_PASS_CONFIRMER = "PROFILE_MW.SHOW_PASS_CONFIRMER";
export const SHOW_MESSAGE_PROMPT = "PROFILE_MW.SHOW_MESSAGE_PROMPT";
export const UPDATE_MATCH_PERCENTAGE = "PROFILE_MW.UPDATE_MATCH_PERCENTAGE";

export const OPEN_LIKES_CAP_MODAL = "PROFILE_MW.OPEN_LIKES_CAP_MODAL";
export const INCREMENT_LIKES_CAP_RATE_CARD_VIEW_COUNT = "PROFILE.INCREMENT_LIKES_CAP_RATE_CARD_VIEW_COUNT";

// reducer
// ----------------------------------------------------------------------------

export function reducer(state = getInitialState(), action) {
	switch (action.type) {
		case PROFILE_LOAD_INITIATED: {
			return {
				...state,
				isLoadingProfile: true,
			};
		}

		case LIKE_INITIATED: {
			return {
				...state,
				isLoadingLike: true,
			};
		}

		case PROFILE_LOAD_CANCELED: {
			return {
				...state,
				isLoadingProfile: false,
			};
		}

		case LIKE_CANCELED: {
			return {
				...state,
				isLoadingLike: false,
			};
		}

		case PROFILE_LOAD_SUCCESS: {
			return {
				...state,
				isLoadingProfile: false,
				...action.payload,
			};
		}

		case LIKE_SUCCESS: {
			if (!action.payload.userid || action.payload.userid !== state.userid) {
				return state;
			}

			const { userInstructions } = state;
			const userInstruction = userInstructions.ocsMessagingLikeProfile;

			const profileComment = action.payload.profileComment && action.payload.profileComment.type === "profile_comment"
				? action.payload.profileComment
				: undefined;

			return {
				...state,
				isLoadingLike: false,
				confirmedPass: false,

				showMessagePrompt: !!userInstruction,
				profileComment,

				// so here's something kinda shitty:
				// the design of the msg prompt doesn't have any CTA
				// because on all other platforms there's a message thinger
				// so let's hack in some CTA text and cancel text
				currentUserInstruction: userInstruction ? {
					...userInstruction,
					ctaText: "Add message",
					cancelText: "Message later",
				} : null,

				likes: {
					...state.likes,
					you_like: true,
					passed_on: false,
					they_like: action.payload.theyLike,
				},
			};
		}

		case LIKE_EXPLAINED: {
			const { userInstructions } = state;

			return {
				...state,
				showLikeExplainer: false,
				userInstructions: {
					...userInstructions,
					ocsFirstLike: null,
				},
				userInstruction: null,
			};
		}

		case PASS_INTIATED: {
			return {
				...state,
				isLoadingPass: true,
			};
		}

		case PASS_CONFIRMED: {
			return {
				...state,
				confirmedPass: true,
				showPassConfirmer: false,
				userInstruction: null,
			};
		}

		case PASS_EXPLAINED: {
			const { userInstructions } = state;

			return {
				...state,
				showPassExplainer: false,
				userInstructions: {
					...userInstructions,
					ocsPass: null,
				},
				userInstruction: null,
			};
		}

		case PASS_CANCELED: {
			return {
				...state,
				isLoadingPass: false,
			};
		}

		case PASS_SUCCESS: {
			if (!action.payload.userid || action.payload.userid !== state.userid) {
				return state;
			}

			return {
				...state,
				isLoadingPass: false,
				likes: {
					...state.likes,
					you_like: false,
					passed_on: true,
				},

				likedObjectType: null,
				likedObject: null,
			};
		}

		case SHOW_LIKE_EXPLAINER: {
			const { userInstructions } = state;
			const userInstruction = userInstructions.ocsFirstLike;
			return {
				...state,
				showLikeExplainer: !!userInstruction,
				currentUserInstruction: userInstruction,
			};
		}

		case TOGGLE_UNMATCH_EXPLAINER: {
			const { userInstructions, showUnmatchExplainer } = state;
			const userInstruction = userInstructions.unmatch;

			return {
				...state,
				showUnmatchExplainer: !showUnmatchExplainer,
				currentUserInstruction: userInstruction,
			};
		}

		case SHOW_PASS_EXPLAINER: {
			const { userInstructions } = state;
			const userInstruction = userInstructions.ocsPass;

			return {
				...state,
				showPassExplainer: !!userInstruction,
				currentUserInstruction: userInstruction,
			};
		}

		case SHOW_PASS_CONFIRMER: {
			const { userInstructions } = state;
			const userInstruction = userInstructions.ocsPassPreviouslyLiked;
			return {
				...state,
				showPassConfirmer: !!userInstruction,
				currentUserInstruction: userInstruction,
			};
		}

		case MESSAGE_PROMPT_DISMISSED: {
			return {
				...state,
				showMessagePrompt: false,
				userInstruction: null,
			};
		}

		case BLOCK_SUCCESS: {
			if (!action.payload.userid || action.payload.userid !== state.userid) {
				return state;
			}

			return {
				...state,
				blocked: true,
			};
		}

		case UNBLOCK_SUCCESS: {
			if (!action.payload.userid || action.payload.userid !== state.userid) {
				return state;
			}

			return {
				...state,
				blocked: false,
			};
		}

		case OPEN_LIKES_CAP_MODAL: {
			const { payload: { isLikesCapModalOpen } } = action;
			return {
				...state,
				isLoadingLike: false,
				isLikesCapModalOpen,
			};
		}

		case INCREMENT_LIKES_CAP_RATE_CARD_VIEW_COUNT: {
			const {
				payload: { likesCapRateCardViewCount },
			} = action;

			return {
				...state,
				likesCapRateCardViewCount,
			};
		}

		case UPDATE_MATCH_PERCENTAGE: {
			const newMatchPercentage = action.payload.matchPercentage;

			return {
				...state,
				percentages: state.percentages ? {
					...state.percentages,
					match: newMatchPercentage,
				} : state.percentages,
			};
		}

		default:
			return state;
	}
}

// actions
// ----------------------------------------------------------------------------

export function loadProfile() {
	const userid = GetUserIdFromWindowLocation() || Context.get("userid");
	const isSelfview = userid === Context.get("userid");

	return (dispatch, getState) => {
		const loadedProfile = getState().profileMW;

		if (loadedProfile.userid === userid) {
			return Promise.resolve();
		}

		dispatch({ type: PROFILE_LOAD_INITIATED });

		dispatch(getUserInfo(userid));
		dispatch(setSelfview(isSelfview));

		subscribeToOkEvent(OKEVENT.Block, ({ tuid }) => {
			dispatch({ type: BLOCK_SUCCESS, payload: { userid: tuid } });
		});

		subscribeToOkEvent(OKEVENT.Unblock, ({ tuid }) => {
			dispatch({ type: UNBLOCK_SUCCESS, payload: { userid: tuid } });
		});

		return OkC.get(`/profile/${userid}`, {
			data: {
				useNewDetailsFormat: true,
			},
		}).then((payload) => {
			const updatedState = get(payload, "extras.reduxParams.profile") || {};

			dispatch({
				type: PROFILE_LOAD_SUCCESS,
				payload: updatedState,
			});
		}).catch(() => {
			dispatch({ type: PROFILE_LOAD_CANCELED });
		});
	};
}

export function openLikesCapModal(isOpen) {
	return {
		type: OPEN_LIKES_CAP_MODAL,
		payload: {
			isLikesCapModalOpen: isOpen,
		},
	};
}

export function like(profileComment) {
	return (dispatch, getState) => {
		const state = getState();
		const {
			likes,
			percentages,
			isLoadingLike,
			userid,
			firstMessage,
			userInstructions,
		} = state.profileMW;
		const { lastUserData: userData } = state.quickmatch;

		const {
			you_like: youLike,
		} = likes;

		if (!youLike && !isLoadingLike) {
			// first like instruction
			if (userInstructions.ocsFirstLike) {
				dispatch({ type: SHOW_LIKE_EXPLAINER });
				return;
			}

			dispatch({ type: LIKE_INITIATED });

			OkC.post("/likes/batch", {
				data: {
					source: "PROFILE",
					votes: [
						{
							userid,
							like: true,
							time_from_request: 0,
							user_data: userData,
						},
					],
				},
			}).then(({ results, shouldTrackHitLikesCap }) => {
				const [voteSucceeded] = results;

				if (shouldTrackHitLikesCap) {
					Analytics.logUIEvent(Analytics.eventNames.reachedDailyLikes);
				}

				if (!voteSucceeded) {
					dispatch(openLikesCapModal(true));

					Analytics.logUIEvent(Analytics.eventNames.votedAfterLikesCapLimit, {
						data: {
							"promo id": "alist.likescappaywall",
							"promo design": getValueForPromoDesign("profilephoto"),
							"target user id": userid,
							"vote value": "like",
							source: "profile",
						},
					});

					// early exit, since the endpoint always returns 200
					return;
				}

				const mutual = voteSucceeded.mutual_like;

				Analytics.logUIEvent(Analytics.eventNames.profileInteraction, {
					type: CUSTOM_EVENT_TYPES.user_content,
					data: {
						interaction: "like",
						userid,
						"resulted in mutual like": mutual ? "yes" : "no",
						"message flag": firstMessage ? "yes" : "no",
						matchPercentage: percentages.match,
						source: "match profile",
					},
				});

				dispatch({
					type: LIKE_SUCCESS,
					payload: {
						userid,
						youLike: true,
						theyLike: mutual,
						profileComment,
					},
				});

				dispatch(processProfileVote(userid, true));
				dispatch(removeUserFromQueues(userid));
			}).catch(() => {
				dispatch({ type: LIKE_CANCELED });
			});
		}
	};
}

export function confirmPass() {
	return { type: PASS_CONFIRMED };
}

export function markLikeExplained() {
	return (dispatch, getState) => {
		const { userInstructions } = getState().profileMW;
		const userInstruction = userInstructions.ocsFirstLike;
		const { userGuide } = userInstruction;

		if (userGuide) {
			OkC.post(`/user_guides/${userGuide}/understands`);
		}

		dispatch({ type: LIKE_EXPLAINED });
	};
}

export function markPassExplained() {
	return (dispatch, getState) => {
		const { userInstructions } = getState().profileMW;
		const userInstruction = userInstructions.ocsPass;
		const { userGuide } = userInstruction;

		if (userGuide) {
			OkC.post(`/user_guides/${userGuide}/understands`);
		}

		dispatch({ type: PASS_EXPLAINED });
	};
}

export function pass({ history } = {}) {
	return (dispatch, getState) => {
		const state = getState();
		const {
			likes,
			percentages,
			userid,
			isLoadingPass,
			confirmedPass,
			firstMessage,
			userInstructions,
		} = state.profileMW;
		const { lastUserData: userData } = state.quickmatch;

		const {
			passed_on: passedOn,
			you_like: youLike,
		} = likes;

		if (!passedOn && !isLoadingPass) {
			// first pass instruction
			if (userInstructions.ocsPass) {
				dispatch({ type: SHOW_PASS_EXPLAINER });
				return;
			}

			// if you already liked them, make then confirm the pass
			if (youLike && !confirmedPass) {
				dispatch({ type: SHOW_PASS_CONFIRMER });
				return;
			}

			dispatch({ type: PASS_INTIATED });

			OkC.post("/likes/batch", {
				data: {
					source: "PROFILE",
					votes: [
						{
							userid,
							like: false,
							time_from_request: 0,
							user_data: userData,
						},
					],
				},
			}).then((res) => {
				if (res.likesRemaining === 0) {
					Analytics.logUIEvent(Analytics.eventNames.votedAfterLikesCapLimit, {
						data: {
							"promo id": "alist.likescappaywall",
							"promo design": getValueForPromoDesign("profilephoto"),
							"target user id": userid,
							"vote value": "pass",
							source: "profile",
						},
					});
				}

				Analytics.logUIEvent(Analytics.eventNames.profileInteraction, {
					type: CUSTOM_EVENT_TYPES.user_content,
					data: {
						interaction: "pass",
						userid,
						"resulted in mutual like": "no",
						"message flag": firstMessage ? "yes" : "no",
						matchPercentage: percentages.match,
						source: "match profile",
					},
				});

				dispatch({ type: PASS_SUCCESS, payload: { userid } });
				dispatch(processProfileVote(userid, false));
				dispatch(removeUserFromQueues(userid));
				redirectToWhereTheyCameFrom(history);
			}).
				catch(() => {
					dispatch({ type: PASS_CANCELED });
				});
		}
	};
}

export function toggleUnmatchConfirmation() {
	return (dispatch) => {
		dispatch({ type: TOGGLE_UNMATCH_EXPLAINER });
		return;
	};
}

export function unmatch() {
	return (dispatch, getState) => {
		const {
			userid,
			percentages,
		} = getState().profileMW;

		dispatch(processProfileVote(userid, false));

		OkC.post(`/profile/${userid}/block`).
			then(() => {
				dispatch(removeUserFromQueues(userid));
				Analytics.logUIEvent(Analytics.eventNames.profileBlock, {
					data: {
						source: "match profile",
						value: "unmatched",
						userid,
						matchPercentage: percentages.match,
					},
				});

				window.location = "/messages";
			});
	};
}

export function message(goToUrl) {
	return (dispatch, getState) => {
		const {
			mobileMessageLink,
			profileComment,
		} = getState().profileMW;

		if (profileComment) {
			// eslint-disable-next-line no-use-before-define
			dispatch(messageWithObject({ profileComment }, goToUrl));
			return;
		}

		if (mobileMessageLink) {
			goToUrl(mobileMessageLink);
		}
	};
}

export function messageWithObject({ profileComment }, goToUrl) {
	return (dispatch, getState) => {
		const {
			userid,
			mobileMessageLink,
		} = getState().profileMW;

		// Update the draft to contain the profileComment.
		return MessagingApi.saveMessageDraft({
			userid,
			profileComment,
			message: "",
		}).then(() => { // Then redirect.
			if (mobileMessageLink) {
				goToUrl(mobileMessageLink);
			}
		});
	};
}

export function dismissMessagePrompt() {
	return { type: MESSAGE_PROMPT_DISMISSED };
}

export function reportPhoto(photoId) {
	return (dispatch, getState) => {
		const { userid } = getState().profileMW;

		// dispatch({ type: REPORT_PHOTO });
		ProfileActions.reportPhoto(userid, photoId);
	};
}

export function block() {
	return (dispatch, getState) => {
		const { userid, percentages } = getState().profileMW;
		ProfileActions.block(userid, { matchPercentage: percentages.match });
		dispatch({ type: BLOCK_INITIATED });
		dispatch(processProfileVote(userid, false));
		dispatch(removeUserFromQueues(userid));
	};
}

export function unblock() {
	return (dispatch, getState) => {
		const { userid } = getState().profileMW;
		ProfileActions.unblock(userid);
		dispatch({ type: UNBLOCK_INITIATED });
	};
}

export function report() {
	return (dispatch, getState) => {
		const { userid } = getState().profileMW;
		Report.open({
			type: TYPES.PROFILE,
			userid,
		});

		dispatch({ type: REPORT_INITIATED });
	};
}

export function updateMatchPercentage(matchPercentage) {
	return {
		type: UPDATE_MATCH_PERCENTAGE,
		payload: {
			matchPercentage,
		},
	};
}

function _incrementLikesCapRateCardViewCount(dispatch, count) {
	dispatch({
		type: INCREMENT_LIKES_CAP_RATE_CARD_VIEW_COUNT,
		payload: {
			likesCapRateCardViewCount: count,
		},
	});
}


export function onIncrementLikesCapModalViewCount() {
	return (dispatch, getStore) => {
		const store = getStore();
		_incrementLikesCapRateCardViewCount(dispatch, store.profileMW.likesCapRateCardViewCount + 1);

		OkC.post("/rate_card/view", {
			version: 1,
			data: {
				promo_id: "alist.likescappaywall",
			},
		})
			.then(({ view_count: viewCount }) => {
				_incrementLikesCapRateCardViewCount(dispatch, viewCount);
			});
	};
}



// WEBPACK FOOTER //
// ./src/ducks/profile_mw.js